
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BlogHeader from '../components/BlogHeader';

// ============================================================
// CRITICAL: Do NOT add blog data to this file.
// Blog posts are stored in the Django database and served via API.
// To create a new blog post, POST to /api/blogs/ on the backend.
// This file only fetches and renders — it contains zero blog content.
// ============================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function renderMarkdown(content) {
  if (!content) return null;
  return content.split('\n').map((line, i) => {
    if (line.startsWith('## ')) return <h2 key={i} style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 22, color: '#F1F5F9', marginTop: 32, marginBottom: 12 }}>{line.replace('## ', '')}</h2>;
    if (line.startsWith('### ')) return <h3 key={i} style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, color: '#F1F5F9', marginTop: 24, marginBottom: 8 }}>{line.replace('### ', '')}</h3>;
    if (line.startsWith('- ')) return <li key={i} style={{ marginBottom: 6, marginLeft: 20 }}>{line.replace('- ', '')}</li>;
    if (line.match(/^\d+\./)) return <li key={i} style={{ marginBottom: 6, marginLeft: 20 }}>{line.replace(/^\d+\.\s*/, '')}</li>;
    if (line.startsWith('**') && line.endsWith('**')) return <p key={i} style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#F1F5F9' }}>{line.replace(/\*\*/g, '')}</p>;
    if (line.includes('**')) {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return <p key={i} style={{ marginBottom: 8 }}>{parts.map((p, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#F1F5F9' }}>{p}</strong> : p)}</p>;
    }
    if (line.startsWith('[') && line.includes('](')) {
      const match = line.match(/\[(.*?)\]\((.*?)\)/);
      if (match) return <p key={i} style={{ marginTop: 24 }}><a href={match[2]} style={{ color: '#F97316', textDecoration: 'none', fontWeight: 700 }}>{match[1]} &rarr;</a></p>;
    }
    if (line.trim() === '') return <br key={i} />;
    return <p key={i} style={{ marginBottom: 8 }}>{line}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/blogs/${slug}/`)
      .then(res => {
        if (res.status === 404) {
          setError('not_found');
          setLoading(false);
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data) {
          setPost(data);
          setLoading(false);
        }
      })
      .catch(() => {
        setError('load_failed');
        setLoading(false);
      });
  }, [slug]);

  // Loading state
  if (loading) {
    return (
      <div style={{ height: '100vh', overflow: 'auto', background: '#0D1117', color: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, color: '#9CA3AF' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Error / not found state
  if (error === 'not_found' || error === 'load_failed' || !post) {
    return (
      <div style={{ height: '100vh', overflow: 'auto', background: '#0D1117', color: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, marginBottom: 12 }}>Post not found</h1>
          <button onClick={() => navigate('/blogs')} style={{ color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
            &larr; Back to blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', overflow: 'auto', background: '#0D1117', fontFamily: 'Inter, sans-serif', color: '#E2E8F0' }}>
      <BlogHeader />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '100px 24px 80px' }}>
        <button onClick={() => navigate('/blogs')} style={{ color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, marginBottom: 24 }}>
          &larr; Back to blog
        </button>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <span style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{post.category}</span>
          <span style={{ color: '#64748B', fontSize: 12 }}>{post.readTime}</span>
        </div>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, marginBottom: 12, lineHeight: 1.3 }}>{post.title}</h1>
        <p style={{ color: '#64748B', fontSize: 13, marginBottom: 40 }}>{post.date}</p>
        <div style={{ fontSize: 16, lineHeight: 1.8, color: '#CBD5E1' }}>
          {renderMarkdown(post.content)}
        </div>
        <div style={{ marginTop: 60, padding: '32px', background: '#1A2236', borderRadius: 16, textAlign: 'center' }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Ready to try Multi Author?</h3>
          <p style={{ color: '#9CA3AF', marginBottom: 20 }}>Start creating content with your AI writing team — free tier available.</p>
          <a href="/register" style={{ display: 'inline-block', padding: '12px 32px', background: '#F97316', color: 'white', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>Get Started Free &rarr;</a>
        </div>
      </div>
    </div>
  );
}
